import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useLang } from '@/context/LangContext';
import { Shield, UserPlus, UserMinus, RefreshCw, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin Premium Manager — UI for the X-Admin-Token-protected endpoints in
 * routes_admin.py (grant-premium, revoke-premium, premium-users, audit-log).
 *
 * The token is NEVER baked into the bundle. The admin enters it once per
 * session; we keep it in sessionStorage (cleared on tab close) instead of
 * localStorage to reduce blast radius if the laptop is stolen.
 *
 * Why a separate UI from /admin? Because /admin focuses on platform stats
 * and content moderation. Premium grants are a different mental model — a
 * dedicated page keeps each action one click away.
 */
const TOKEN_KEY = 'pm_admin_grant_token';

export default function AdminPremium() {
  const { t } = useLang();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [showToken, setShowToken] = useState(false);

  // Grant form
  const [grantEmail, setGrantEmail] = useState('');
  const [planId, setPlanId] = useState('premium_annual');
  const [days, setDays] = useState(365);
  const [granting, setGranting] = useState(false);

  // Revoke form
  const [revokeEmail, setRevokeEmail] = useState('');
  const [revoking, setRevoking] = useState(false);

  // List + audit
  const [activeUsers, setActiveUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);

  // Persist token across reloads of the same tab.
  useEffect(() => {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  }, [token]);

  const adminFetch = async (path, opts = {}) => {
    if (!token) throw new Error('admin_token_missing');
    const backend = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${backend}/api/admin${path}`, {
      ...opts,
      headers: {
        'X-Admin-Token': token,
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = data?.detail || `HTTP ${res.status}`;
      throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
    return data;
  };

  const refresh = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [list, log] = await Promise.all([
        adminFetch('/premium-users'),
        adminFetch('/audit-log?limit=20'),
      ]);
      setActiveUsers(list?.users || []);
      setAudit(log?.items || []);
    } catch (err) {
      toast.error(err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh whenever a fresh token lands.
  useEffect(() => {
    if (token) refresh();
  }, [token]);

  const doGrant = async (e) => {
    e?.preventDefault?.();
    if (!grantEmail) return;
    setGranting(true);
    try {
      const data = await adminFetch('/grant-premium', {
        method: 'POST',
        body: JSON.stringify({ email: grantEmail, plan_id: planId, days: Number(days) || 365 }),
      });
      toast.success(`Activado: ${data.email} → ${data.plan} hasta ${data.expires_at?.slice(0, 10)}`);
      setGrantEmail('');
      refresh();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setGranting(false);
    }
  };

  const doRevoke = async (emailToRevoke) => {
    const targetEmail = emailToRevoke || revokeEmail;
    if (!targetEmail) return;
    if (!window.confirm(`¿Seguro que querés revocar el plan de ${targetEmail}?`)) return;
    setRevoking(true);
    try {
      await adminFetch('/revoke-premium', {
        method: 'POST',
        body: JSON.stringify({ email: targetEmail }),
      });
      toast.success(`Plan revocado: ${targetEmail}`);
      if (!emailToRevoke) setRevokeEmail('');
      refresh();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setRevoking(false);
    }
  };

  const tokenMissing = !token;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="admin-premium-page">
        <div className="flex items-center gap-3 mb-1">
          <Shield className="w-6 h-6 text-gold" />
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold">Admin Tools</div>
        </div>
        <h1 className="font-h uppercase text-4xl mb-2">Premium Manager</h1>
        <p className="text-gray-400 mb-6">
          Activá, revocá o auditá planes premium manualmente sin pasar por Stripe. Las acciones quedan
          registradas en el audit log.
        </p>

        {/* TOKEN INPUT */}
        <div className={`rounded-lg border-2 p-4 mb-6 ${tokenMissing ? 'border-red-400/60 bg-red-500/5' : 'border-gold/30 bg-card-steel'}`}>
          <label className="text-xs font-mono uppercase tracking-widest text-gold/80 block mb-2">
            Admin Token (X-Admin-Token)
          </label>
          <div className="flex items-center gap-2">
            <input
              type={showToken ? 'text' : 'password'}
              value={token}
              onChange={(e) => setToken(e.target.value.trim())}
              placeholder="Pegá el secret de ADMIN_GRANT_SECRET"
              className="flex-1 bg-navy border border-gold/30 rounded px-3 py-2 text-sm text-white outline-none focus:border-gold font-mono"
              data-testid="admin-token-input"
            />
            <button
              type="button"
              onClick={() => setShowToken(s => !s)}
              className="p-2 rounded border border-gold/30 text-gold hover:bg-gold/10"
              title={showToken ? 'Ocultar' : 'Mostrar'}
            >
              {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={!token || loading}
              className="px-3 py-2 rounded border border-gold/30 text-gold hover:bg-gold/10 inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Verificar
            </button>
          </div>
          {tokenMissing && (
            <div className="flex items-start gap-2 mt-3 text-red-300 text-xs">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              Ingresá el token para habilitar las acciones. Lo guardo solo en `sessionStorage` (se borra al cerrar la pestaña).
            </div>
          )}
        </div>

        {/* GRANT + REVOKE forms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <form
            onSubmit={doGrant}
            className="rounded-lg border-2 border-gold/40 bg-card-steel p-5"
            data-testid="admin-grant-form"
          >
            <div className="flex items-center gap-2 mb-3">
              <UserPlus className="w-5 h-5 text-gold" />
              <div className="font-h uppercase text-lg">Activar Premium</div>
            </div>
            <label className="text-xs font-mono uppercase tracking-widest text-gold/70 block mb-1">Email</label>
            <input
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="usuario@dominio.com"
              required
              className="w-full bg-navy border border-gold/30 rounded px-3 py-2 text-sm text-white outline-none focus:border-gold mb-3"
              data-testid="admin-grant-email"
            />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-gold/70 block mb-1">Plan</label>
                <select
                  value={planId}
                  onChange={(e) => setPlanId(e.target.value)}
                  className="w-full bg-navy border border-gold/30 rounded px-3 py-2 text-sm text-white outline-none focus:border-gold"
                  data-testid="admin-grant-plan"
                >
                  <option value="premium_annual">Premium Anual ($249.99)</option>
                  <option value="premium_monthly">Premium Mensual ($14.99)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-gold/70 block mb-1">Días</label>
                <input
                  type="number"
                  min={1}
                  max={3650}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="w-full bg-navy border border-gold/30 rounded px-3 py-2 text-sm text-white outline-none focus:border-gold"
                  data-testid="admin-grant-days"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={!token || granting || !grantEmail}
              data-testid="admin-grant-submit"
              className="w-full bg-gold text-navy font-h uppercase tracking-wider py-2.5 rounded hover:bg-gold-light disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {granting ? 'Activando…' : 'Activar'}
            </button>
          </form>

          <form
            onSubmit={(e) => { e.preventDefault(); doRevoke(); }}
            className="rounded-lg border-2 border-red-400/40 bg-card-steel p-5"
            data-testid="admin-revoke-form"
          >
            <div className="flex items-center gap-2 mb-3">
              <UserMinus className="w-5 h-5 text-red-300" />
              <div className="font-h uppercase text-lg">Revocar Premium</div>
            </div>
            <label className="text-xs font-mono uppercase tracking-widest text-red-300/80 block mb-1">Email</label>
            <input
              type="email"
              value={revokeEmail}
              onChange={(e) => setRevokeEmail(e.target.value)}
              placeholder="usuario@dominio.com"
              required
              className="w-full bg-navy border border-red-400/30 rounded px-3 py-2 text-sm text-white outline-none focus:border-red-400 mb-4"
              data-testid="admin-revoke-email"
            />
            <button
              type="submit"
              disabled={!token || revoking || !revokeEmail}
              data-testid="admin-revoke-submit"
              className="w-full bg-red-500/20 text-red-200 border border-red-400/60 font-h uppercase tracking-wider py-2.5 rounded hover:bg-red-500/30 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {revoking ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
              {revoking ? 'Revocando…' : 'Revocar'}
            </button>
          </form>
        </div>

        {/* ACTIVE PREMIUM USERS TABLE */}
        <div className="rounded-lg border border-gold/30 bg-card-steel mb-6 overflow-hidden">
          <div className="px-5 py-3 border-b border-gold/20 flex items-center justify-between">
            <div className="font-h uppercase text-lg">Usuarios premium activos ({activeUsers.length})</div>
            <button
              onClick={refresh}
              disabled={loading || !token}
              className="text-xs text-gold/80 hover:text-gold inline-flex items-center gap-1"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refrescar
            </button>
          </div>
          <div className="overflow-x-auto" data-testid="admin-active-users">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-mono uppercase text-gold/70 border-b border-gold/15">
                  <th className="px-5 py-2">Email</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Plan</th>
                  <th className="px-3 py-2">Vence</th>
                  <th className="px-3 py-2 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {activeUsers.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-500">Sin usuarios premium activos.</td></tr>
                )}
                {activeUsers.map(u => (
                  <tr key={u.id} className="border-b border-gold/10 hover:bg-gold/5">
                    <td className="px-5 py-2.5 text-white">{u.email}</td>
                    <td className="px-3 py-2.5 text-gray-300">{u.role}</td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full bg-gold/15 border border-gold/40 text-gold text-xs font-mono uppercase tracking-widest">
                        {u.plan}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-400">{(u.plan_expires_at || '').slice(0, 10)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        onClick={() => doRevoke(u.email)}
                        className="text-xs text-red-300 hover:text-red-200"
                        data-testid={`admin-revoke-${u.id}`}
                      >
                        Revocar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* AUDIT LOG */}
        <div className="rounded-lg border border-gold/30 bg-card-steel overflow-hidden">
          <div className="px-5 py-3 border-b border-gold/20 font-h uppercase text-lg">
            Audit log (últimas {audit.length})
          </div>
          <div className="overflow-x-auto" data-testid="admin-audit-log">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-mono uppercase text-gold/70 border-b border-gold/15">
                  <th className="px-5 py-2">Acción</th>
                  <th className="px-3 py-2">Email objetivo</th>
                  <th className="px-3 py-2">Plan</th>
                  <th className="px-3 py-2">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-6 text-center text-gray-500">Sin acciones todavía.</td></tr>
                )}
                {audit.map((a, i) => {
                  const isGrant = a.action === 'grant_premium';
                  const when = a.granted_at || a.revoked_at || '';
                  return (
                    <tr key={i} className="border-b border-gold/10">
                      <td className="px-5 py-2.5">
                        <span className={`px-2 py-0.5 rounded text-xs font-mono uppercase tracking-widest ${
                          isGrant ? 'bg-green-500/10 text-green-300 border border-green-400/40' : 'bg-red-500/10 text-red-300 border border-red-400/40'
                        }`}>
                          {isGrant ? 'grant' : 'revoke'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-white">{a.target_email}</td>
                      <td className="px-3 py-2.5 text-gray-300">{a.plan_id || a.previous_plan || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-400 font-mono">{when.replace('T', ' ').slice(0, 19)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
